import { Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
    History,
    Home,
    LayoutGrid,
    LogOut,
    Menu,
    QrCode,
    ShieldCheck,
} from "lucide-react";
import { useEffect, useState } from "react";
import { ThemeToggle } from "../components/ThemeToggle";
import { AdminMobileHint } from "../components/ViewportGuard";
import { PhotoQueueBadge } from "../components/photo/PhotoQueueBadge";
import { ScanFab } from "../components/mobile/ScanFab";
import { useAuth } from "../context/AuthContext";
import { usePendingOps } from "../hooks/useApi";
import { usePhotoQueueWorker } from "../hooks/usePhotoQueueWorker";
import { useOpQueueWorker } from "../hooks/useOpQueueWorker";

const TABS = [
    { to: "/", label: "Start", icon: Home, testid: "tab-start" },
    { to: "/scan", label: "Scan", icon: QrCode, testid: "tab-scan" },
    { to: "/aktionen", label: "Aktionen", icon: LayoutGrid, testid: "tab-aktionen" },
    { to: "/verlauf", label: "Verlauf", icon: History, testid: "tab-verlauf" },
    { to: "/mehr", label: "Mehr", icon: Menu, testid: "tab-mehr" },
];

export default function MobileShell() {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user, signOut, role } = useAuth();
    const { pendingOps } = usePendingOps();
    const [showAdminHint, setShowAdminHint] = useState(false);
    usePhotoQueueWorker();
    useOpQueueWorker();

    useEffect(() => {
        if (searchParams.get("admin_hint") === "1") {
            setShowAdminHint(true);
        }
    }, [searchParams]);

    const dismissHint = () => {
        setShowAdminHint(false);
        const next = new URLSearchParams(searchParams);
        next.delete("admin_hint");
        setSearchParams(next, { replace: true });
    };

    return (
        <div className="pw-grain flex min-h-screen w-full flex-col bg-background text-foreground">
            {/* Header */}
            <header className="sticky top-0 z-30 flex items-center justify-between border-b border-border bg-card/80 px-4 py-2 backdrop-blur">
                <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground">
                        <span className="text-xs font-black tracking-tighter">P</span>
                    </div>
                    <div className="leading-tight">
                        <div className="text-xs font-semibold text-foreground">Platzwart</div>
                        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                            Floor Mode
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-1">
                    {role === "admin" ? (
                        <span
                            className="pw-tap flex items-center justify-center rounded-md text-primary"
                            role="img"
                            aria-label="Admin"
                            data-testid="admin-badge"
                        >
                            <ShieldCheck className="h-5 w-5" aria-hidden="true" />
                        </span>
                    ) : null}
                    {pendingOps > 0 ? (
                        <button
                            type="button"
                            onClick={() => navigate("/mehr/queue")}
                            className="rounded-full border border-warning/50 bg-[rgb(var(--warning-bg))] px-2 py-0.5 text-xs uppercase tracking-wider text-warning hover:border-warning"
                            data-testid="pending-ops-chip"
                        >
                            {pendingOps} ausstehend
                        </button>
                    ) : null}
                    <PhotoQueueBadge onClick={() => navigate("/mehr/fotos-queue")} />
                    <ThemeToggle />
                    <button
                        type="button"
                        onClick={async () => {
                            await signOut();
                            navigate("/login", { replace: true });
                        }}
                        className="pw-tap flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground"
                        aria-label="Abmelden"
                        data-testid="mobile-logout-btn"
                    >
                        <LogOut className="h-5 w-5" />
                    </button>
                </div>
            </header>

            {/* Greeting strip */}
            {user ? (
                <div className="border-b border-border bg-card/40 px-4 py-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    <span className="text-primary">{role === "admin" ? "Admin" : "User"}</span>
                    <span className="mx-2 opacity-40">//</span>
                    <span>{user.email}</span>
                </div>
            ) : null}

            <main className="flex-1 overflow-y-auto px-4 py-5 pb-28">
                {showAdminHint ? (
                    <div className="mb-4">
                        <AdminMobileHint onDismiss={dismissHint} />
                    </div>
                ) : null}
                <Outlet />
            </main>

            {/* Bottom Tab */}
            <nav
                className="fixed bottom-0 left-0 right-0 z-30 border-t border-border bg-card/95 backdrop-blur"
                data-testid="mobile-bottom-tabs"
            >
                <ul className="mx-auto grid max-w-md grid-cols-5">
                    {TABS.map((tab) => {
                        const Icon = tab.icon;
                        const active =
                            tab.to === "/"
                                ? location.pathname === "/"
                                : location.pathname.startsWith(tab.to);
                        return (
                            <li key={tab.to}>
                                <button
                                    type="button"
                                    onClick={() => navigate(tab.to)}
                                    className={`pw-tap flex w-full flex-col items-center justify-center gap-0.5 py-2 text-xs uppercase tracking-wider transition-colors ${
                                        active
                                            ? "text-primary"
                                            : "text-muted-foreground hover:text-foreground"
                                    }`}
                                    data-testid={tab.testid}
                                >
                                    <Icon className="h-5 w-5" />
                                    <span>{tab.label}</span>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            </nav>
            <ScanFab />
        </div>
    );
}
